(() => {
  const { location } = window;
  if (!location) return;

  const currentUrl = new URL(location.href);
  const scriptCanonicalUrl =
    typeof document !== 'undefined'
      ? document.currentScript?.dataset?.canonicalUrl || ''
      : '';
  const hasTrackingException = [...currentUrl.searchParams.keys()].some(key => {
    const normalizedKey = key.toLowerCase();
    return normalizedKey.startsWith('utm_') || normalizedKey === 'gclid';
  });

  const collapseSlashes = path => (path || '/').replace(/\/{2,}/g, '/');
  const stripTrailingSlash = path => path.replace(/\/+$/, '') || '/';
  const ROOT_INDEX_PATH_REGEXP = /^\/index(?:\.(?:html?|php))?\/?$/i;
  const SERVICE_TRAILING_SEGMENT_REGEXP =
    /^(?:home|index)(?:\.(?:html?|php))?$/i;
  const SERVICE_EXTENSION_REGEXP = /\.(?:html?|php)$/i;

  const normalizeGenericSegment = segment => {
    if (!segment) return '';

    return segment
      .replace(/%2a/gi, '')
      .replace(/\*/g, '')
      .replace(SERVICE_EXTENSION_REGEXP, '')
      .trim();
  };

  const sanitizePathPreservingCase = path => {
    const collapsedPath = collapseSlashes(path);

    if (ROOT_INDEX_PATH_REGEXP.test(collapsedPath)) {
      return '/';
    }

    const segments = collapsedPath
      .split('/')
      .filter(Boolean)
      .map(normalizeGenericSegment)
      .filter(Boolean);

    while (
      segments.length > 1 &&
      SERVICE_TRAILING_SEGMENT_REGEXP.test(segments[segments.length - 1])
    ) {
      segments.pop();
    }

    const sanitizedPath = segments.length ? `/${segments.join('/')}` : '/';
    return stripTrailingSlash(sanitizedPath);
  };

  const normalizeProductHandle = rawHandle => {
    const preparedHandle = normalizeGenericSegment(rawHandle).toLowerCase();

    return preparedHandle
      .replace(/_/g, '-')
      .replace(/[^a-z0-9-]/g, '')
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const normalizeCollectionHandle = rawHandle => {
    const preparedHandle = normalizeGenericSegment(rawHandle).toLowerCase();

    return preparedHandle
      .replace(/_/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const normalizeGenericPathSegmentForCanonical = segment =>
    normalizeGenericSegment(segment).toLowerCase().replace(/_/g, '-');

  const comparePathKey = path =>
    sanitizePathPreservingCase(path).toLowerCase().replace(/_/g, '-');

  const normalizePathKey = path => comparePathKey(path);

  const detectBlogHomePath = () => {
    if (typeof document === 'undefined') return '/blogs/news';

    const blogLinks = document.querySelectorAll('a[href*="/blogs/"]');

    for (const blogLink of blogLinks) {
      const href = blogLink.getAttribute('href');
      if (!href) continue;

      try {
        const parsedHref = new URL(href, currentUrl.origin);
        const matchedBlogPath =
          parsedHref.pathname.match(/^\/blogs\/([^/?#]+)/i);

        if (matchedBlogPath?.[1]) {
          return `/blogs/${matchedBlogPath[1].toLowerCase()}`;
        }
      } catch (error) {}
    }

    return '/blogs/news';
  };

  const explicitRedirectPathByPath = {
    [normalizePathKey('/collections/frontpage')]: '/'
  };

  const resolveExplicitRedirectTarget = pathKey => {
    if (
      pathKey.includes('/blog/author/') ||
      pathKey.includes('/blogs/author/')
    ) {
      return detectBlogHomePath();
    }

    if (pathKey.endsWith('/collections/frontpage')) {
      return '/';
    }

    return explicitRedirectPathByPath[pathKey] || '';
  };

  const currentPathKey = normalizePathKey(currentUrl.pathname);
  const explicitRedirectTarget = resolveExplicitRedirectTarget(currentPathKey);

  if (explicitRedirectTarget) {
    const normalizedRedirectTarget = sanitizePathPreservingCase(
      explicitRedirectTarget
    );

    if (normalizePathKey(normalizedRedirectTarget) !== currentPathKey) {
      window.location.replace(
        `${normalizedRedirectTarget}${currentUrl.search}${currentUrl.hash}`
      );
      return;
    }
  }

  if (hasTrackingException) return;

  const buildCanonicalPath = pathname => {
    const sanitizedPath = sanitizePathPreservingCase(pathname);
    const segments = sanitizedPath.split('/').filter(Boolean);
    if (!segments.length) return '/';

    const routeSegment = segments[0].toLowerCase();

    if (routeSegment === 'products') {
      if (segments.length === 1) return '/collections/makeup';

      const normalizedHandle = normalizeProductHandle(segments[1]);
      if (!normalizedHandle) return '/collections/makeup';

      return `/products/${normalizedHandle}`;
    }

    if (routeSegment === 'collections') {
      if (segments.length === 1) return '/collections/makeup';

      const normalizedCollectionPath = `/collections/${segments
        .slice(1)
        .map(normalizeCollectionHandle)
        .filter(Boolean)
        .join('/')}`;

      return sanitizePathPreservingCase(normalizedCollectionPath);
    }

    const normalizedGenericPath = `/${segments
      .map(normalizeGenericPathSegmentForCanonical)
      .filter(Boolean)
      .join('/')}`;

    return sanitizePathPreservingCase(normalizedGenericPath);
  };

  let canonicalPath = buildCanonicalPath(currentUrl.pathname);
  if (scriptCanonicalUrl) {
    try {
      const canonicalUrlFromTheme = new URL(
        scriptCanonicalUrl,
        currentUrl.origin
      );
      const canonicalPathFromTheme = sanitizePathPreservingCase(
        canonicalUrlFromTheme.pathname
      );

      if (
        comparePathKey(canonicalPathFromTheme) === comparePathKey(canonicalPath)
      ) {
        canonicalPath = canonicalPathFromTheme;
      }
    } catch (error) {}
  }

  const currentPathWithSuffix = `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`;
  const canonicalPathWithSuffix = `${canonicalPath}${currentUrl.search}${currentUrl.hash}`;

  if (canonicalPathWithSuffix !== currentPathWithSuffix) {
    window.location.replace(canonicalPathWithSuffix);
  }
})();
